/**
 * Ambulance Order Detail Page
 *
 * Shows full order details with:
 * - All order information
 * - Change history timeline (audit trail)
 * - Edit functionality for authorized users
 * - Review functionality for reviewers
 */
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Plus,
  FileEdit,
  ClipboardCheck,
  MessageCircle,
} from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useOrganization } from '../contexts/OrganizationContext'
import type {
  AmbulanceOrder,
  AmbulanceOrderChange,
  AmbulanceOrderUpdate,
  OrderType,
} from '../types/ambulance'
import { validateFinnishSSN } from '../types/ambulance'

interface CostCenter {
  id: string
  code: string
  name: string
  show_on_ambulance_order?: boolean
}

interface UserInfo {
  id: string
  role: string
  can_order_ambulance?: boolean
  can_review_ambulance?: boolean
}

interface OrderDetailResponse {
  order: AmbulanceOrder
  changes: AmbulanceOrderChange[]
}

export default function AmbulanceOrderDetailPage() {
  const { t } = useTranslation('ambulance')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedOrgId } = useOrganization()

  // Fetch current user info
  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000,
  })

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [commentDialogOpen, setCommentDialogOpen] = useState(false)
  const [reviewComment, setReviewComment] = useState('')
  const [newComment, setNewComment] = useState('')

  // Edit form state
  const [editForm, setEditForm] = useState<AmbulanceOrderUpdate>({})
  const [editComment, setEditComment] = useState('')
  const [ssnError, setSsnError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Cost center search state for edit dialog
  const [editCostCenterSearch, setEditCostCenterSearch] = useState('')
  const [showEditCostCenterDropdown, setShowEditCostCenterDropdown] = useState(false)
  const editCostCenterDropdownRef = useRef<HTMLDivElement>(null)

  // Extract error message from API response
  const getErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
      return t('errors.updateFailed')
    }

    const err = error as { response?: { data?: { detail?: unknown } } }
    const detail = err.response?.data?.detail

    // API error code: {"detail": {"code": "INVALID_COST_CENTER"}}
    if (detail && typeof detail === 'object' && 'code' in detail) {
      const code = (detail as { code: string }).code
      const errorMap: Record<string, string> = {
        'INVALID_COST_CENTER': t('errors.invalidCostCenter'),
        'INVALID_ORGANIZATION': t('errors.updateFailed'),
        'FEATURE_NOT_ENABLED': t('errors.featureNotEnabled'),
        'INSUFFICIENT_PERMISSIONS': t('errors.noPermission'),
        'ORDER_NOT_FOUND': t('errors.orderNotFound'),
        'ORDER_ALREADY_REVIEWED': t('errors.orderAlreadyReviewed'),
        'NOT_ORDER_OWNER': t('errors.notOrderOwner'),
        'ACCESS_DENIED': t('errors.accessDenied'),
      }
      return errorMap[code] || `${t('errors.updateFailed')}: ${code}`
    }

    // Pydantic validation errors: {"detail": [{"loc": ["body", "field"], "msg": "..."}]}
    if (Array.isArray(detail)) {
      const errors: Record<string, string> = {}
      const fieldNameMap: Record<string, string> = {
        'orderer_name': t('form.ordererName'),
        'orderer_phone': t('form.ordererPhone'),
        'patient_name': t('form.patientName'),
        'patient_ssn': t('form.patientSsn'),
        'pickup_location': t('form.pickupLocation'),
        'destination_location': t('form.destinationLocation'),
        'pickup_date': t('form.pickupDate'),
        'pickup_time': t('form.pickupTime'),
        'pickup_department': t('form.pickupDepartment'),
        'cost_center_id': t('form.costCenter'),
        'order_type': t('form.orderType'),
      }

      for (const validationError of detail as Array<{ loc: string[]; msg: string }>) {
        const fieldPath = validationError.loc
        const fieldName = fieldPath[fieldPath.length - 1]
        const localizedField = fieldNameMap[fieldName] || fieldName
        errors[fieldName] = `${localizedField}: ${validationError.msg}`
      }

      setFieldErrors(errors)

      if (Object.keys(errors).length > 0) {
        return Object.values(errors)[0]
      }
    }

    return t('errors.updateFailed')
  }

  // Extract error message for review/comment operations
  const getReviewErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
      return t('errors.reviewFailed')
    }

    const err = error as { response?: { data?: { detail?: unknown } } }
    const detail = err.response?.data?.detail

    if (detail && typeof detail === 'object' && 'code' in detail) {
      const code = (detail as { code: string }).code
      const errorMap: Record<string, string> = {
        'ORDER_NOT_FOUND': t('errors.orderNotFound'),
        'ORDER_ALREADY_REVIEWED': t('errors.orderAlreadyReviewed'),
        'ACCESS_DENIED': t('errors.accessDenied'),
        'INSUFFICIENT_PERMISSIONS': t('errors.noReviewPermission'),
      }
      return errorMap[code] || `${t('errors.reviewFailed')}: ${code}`
    }

    return t('errors.reviewFailed')
  }

  const getCommentErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
      return t('errors.commentFailed')
    }

    const err = error as { response?: { data?: { detail?: unknown } } }
    const detail = err.response?.data?.detail

    if (detail && typeof detail === 'object' && 'code' in detail) {
      const code = (detail as { code: string }).code
      const errorMap: Record<string, string> = {
        'ORDER_NOT_FOUND': t('errors.orderNotFound'),
        'ACCESS_DENIED': t('errors.accessDenied'),
      }
      return errorMap[code] || `${t('errors.commentFailed')}: ${code}`
    }

    return t('errors.commentFailed')
  }

  // Fetch order details with changes
  const { data: orderData, isLoading: orderLoading, error: orderError } = useQuery<OrderDetailResponse>({
    queryKey: ['ambulance-order', id, selectedOrgId],
    queryFn: () => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.get(`/ambulance-orders/${id}${params}`)
    },
    enabled: !!id,
  })

  const order = orderData?.order
  const changes = orderData?.changes || []

  // Fetch cost centers (only those marked for ambulance orders)
  const { data: costCentersData } = useQuery<{ data: CostCenter[] }>({
    queryKey: ['cost-centers', selectedOrgId, 'ambulance'],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedOrgId) params.append('organization_id', selectedOrgId)
      params.append('ambulance_only', 'true')
      return apiClient.get(`/cost-centers?${params.toString()}`)
    },
  })

  // Filter cost centers by search
  const filteredEditCostCenters = costCentersData?.data.filter(cc =>
    cc.code.toLowerCase().includes(editCostCenterSearch.toLowerCase()) ||
    cc.name.toLowerCase().includes(editCostCenterSearch.toLowerCase())
  ) || []

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editCostCenterDropdownRef.current && !editCostCenterDropdownRef.current.contains(event.target as Node)) {
        setShowEditCostCenterDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: AmbulanceOrderUpdate) => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.patch(`/ambulance-orders/${id}${params}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambulance-order', id] })
      setEditDialogOpen(false)
      setEditForm({})
      setEditComment('')
      setFieldErrors({})
      setEditCostCenterSearch('')
    },
    onError: (error: unknown) => {
      getErrorMessage(error)
    },
  })

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: (data: { comment?: string }) => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.post(`/ambulance-orders/${id}/review${params}`, { comment: data.comment || null })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambulance-order', id] })
      setReviewDialogOpen(false)
      setReviewComment('')
    },
  })

  // Comment mutation
  const commentMutation = useMutation({
    mutationFn: (data: { comment: string }) => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.post(`/ambulance-orders/${id}/comment${params}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambulance-order', id] })
      setCommentDialogOpen(false)
      setNewComment('')
    },
  })

  // Permission checks
  // Only system_admin has automatic access, organization_admin needs explicit permission
  const isSystemAdmin = userInfo?.role === 'system_admin'

  const canEdit = () => {
    if (!order || !userInfo) return false
    if (isSystemAdmin || userInfo.can_review_ambulance) {
      return true
    }
    if (userInfo.can_order_ambulance) {
      return order.created_by === userInfo.id && order.status === 'pending'
    }
    return false
  }

  const canReview = () => {
    if (!order || !userInfo) return false
    if (order.status !== 'pending') return false
    return isSystemAdmin || userInfo.can_review_ambulance
  }

  const canViewHistory = () => {
    if (!userInfo) return false
    return isSystemAdmin || userInfo.can_review_ambulance
  }

  // Anyone who can view the order can comment
  const canComment = () => {
    if (!order || !userInfo) return false
    // System admin and reviewers can always comment
    if (isSystemAdmin || userInfo.can_review_ambulance) return true
    // Orderers can comment on their own orders
    if (userInfo.can_order_ambulance && order.created_by === userInfo.id) return true
    return false
  }

  const handleAddComment = () => {
    if (newComment.trim()) {
      commentMutation.mutate({ comment: newComment.trim() })
    }
  }

  const handleOpenEditDialog = () => {
    if (!order) return
    setEditForm({
      order_type: order.order_type,
      orderer_name: order.orderer_name,
      orderer_phone: order.orderer_phone,
      cost_center_id: order.cost_center_id || undefined,
      pickup_department: order.pickup_department,
      patient_name: order.patient_name,
      // order_id_number is auto-generated (starting from 1000), not editable
      patient_ssn: order.patient_ssn,
      pickup_location: order.pickup_location,
      destination_location: order.destination_location,
      pickup_date: order.pickup_date,
      pickup_time: order.pickup_time?.substring(0, 5) || '',
    })
    // Set initial cost center search to show current selection
    if (order.cost_center_id && order.cost_center_name) {
      // Find the cost center to get code and name
      const cc = costCentersData?.data.find(c => c.id === order.cost_center_id)
      if (cc) {
        setEditCostCenterSearch(`${cc.code} - ${cc.name}`)
      } else {
        setEditCostCenterSearch(order.cost_center_name)
      }
    } else {
      setEditCostCenterSearch('')
    }
    setEditComment('')
    setSsnError('')
    setEditDialogOpen(true)
  }

  const handleSaveEdit = () => {
    if (editForm.patient_ssn && !validateFinnishSSN(editForm.patient_ssn)) {
      setSsnError(t('form.ssnInvalid'))
      return
    }
    updateMutation.mutate({
      ...editForm,
      comment: editComment || undefined,
    })
  }

  const handleReview = () => {
    reviewMutation.mutate({ comment: reviewComment || undefined })
  }

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('fi-FI')
  const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleString('fi-FI')
  const formatTime = (timeStr: string | undefined | null) => timeStr?.substring(0, 5) || '-'

  const getChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'create': return <Plus className="w-4 h-4" />
      case 'update': return <FileEdit className="w-4 h-4" />
      case 'review': return <ClipboardCheck className="w-4 h-4" />
      case 'comment': return <MessageCircle className="w-4 h-4" />
      default: return <FileEdit className="w-4 h-4" />
    }
  }

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'create': return 'bg-blue-500'
      case 'update': return 'bg-yellow-500'
      case 'review': return 'bg-green-500'
      case 'comment': return 'bg-purple-500'
      default: return 'bg-gray-500'
    }
  }

  if (orderLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (orderError || !order) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-100 text-red-700 rounded-lg flex items-center mb-4">
          <AlertCircle className="w-5 h-5 mr-2" />
          {t('errors.loadFailed')}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('common.back')}
        </button>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{t('detail.title')}</h1>
          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
            order.status === 'reviewed'
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {order.status === 'reviewed' ? (
              <><CheckCircle className="w-4 h-4" /> {t('status.reviewed')}</>
            ) : (
              <><Clock className="w-4 h-4" /> {t('status.pending')}</>
            )}
          </span>
        </div>
        <div className="flex gap-2">
          {canComment() && (
            <button
              onClick={() => setCommentDialogOpen(true)}
              className="flex items-center px-4 py-2 border border-purple-500 text-purple-700 rounded-lg hover:bg-purple-50"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('comment.addComment')}
            </button>
          )}
          {canEdit() && (
            <button
              onClick={handleOpenEditDialog}
              className="flex items-center px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              {t('common.edit')}
            </button>
          )}
          {canReview() && (
            <button
              onClick={() => setReviewDialogOpen(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('review.markAsReviewed')}
            </button>
          )}
        </div>
      </div>

      <div className={`grid gap-6 ${canViewHistory() ? 'lg:grid-cols-3' : ''}`}>
        {/* Order Details */}
        <div className={canViewHistory() ? 'lg:col-span-2' : ''}>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t('detail.orderInfo')}</h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('form.orderType')}</p>
                <p className="font-medium">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    order.order_type === 'emergency'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {t(`orderType.${order.order_type}`)}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('form.costCenter')}</p>
                <p className="font-medium">{order.cost_center_name || '-'}</p>
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2" />

              <div>
                <p className="text-sm text-gray-500">{t('form.ordererName')}</p>
                <p className="font-medium">{order.orderer_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('form.ordererPhone')}</p>
                <p className="font-medium">{order.orderer_phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('form.pickupDepartment')}</p>
                <p className="font-medium">{order.pickup_department}</p>
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2" />

              <div>
                <p className="text-sm text-gray-500">{t('form.patientName')}</p>
                <p className="font-medium">{order.patient_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderForm.orderIdNumber')}</p>
                <p className="font-medium">{order.order_id_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('form.patientSsn')}</p>
                <p className="font-mono font-medium">{order.patient_ssn}</p>
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2" />

              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">{t('form.pickupLocation')}</p>
                <p className="font-medium">{order.pickup_location}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">{t('form.destinationLocation')}</p>
                <p className="font-medium">{order.destination_location}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('form.pickupDate')}</p>
                <p className="font-medium">{formatDate(order.pickup_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('form.pickupTime')}</p>
                <p className="font-medium">{formatTime(order.pickup_time)}</p>
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2" />

              <div>
                <p className="text-sm text-gray-500">{t('detail.createdBy')}</p>
                <p className="font-medium">{order.created_by_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('detail.createdAt')}</p>
                <p className="font-medium">{formatDateTime(order.created_at)}</p>
              </div>

              {order.status === 'reviewed' && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">{t('detail.reviewedBy')}</p>
                    <p className="font-medium">{order.reviewed_by_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('detail.reviewedAt')}</p>
                    <p className="font-medium">
                      {order.reviewed_at ? formatDateTime(order.reviewed_at) : '-'}
                    </p>
                  </div>
                  {order.review_comment && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">{t('detail.reviewComment')}</p>
                      <p className="font-medium">{order.review_comment}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Change History */}
        {canViewHistory() && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t('detail.changeHistory')}</h2>

            {changes.length === 0 ? (
              <p className="text-gray-500">{t('detail.noChanges')}</p>
            ) : (
              <div className="space-y-4">
                {changes.map((change) => (
                  <div key={change.id} className="flex gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white ${getChangeTypeColor(change.change_type)}`}>
                      {getChangeTypeIcon(change.change_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{t(`changeType.${change.change_type}`)}</p>
                      <p className="text-xs text-gray-500">
                        {change.changed_by_name} - {formatDateTime(change.changed_at)}
                      </p>

                      {Object.keys(change.changes).length > 0 && (
                        <div className="mt-2 text-xs space-y-1">
                          {Object.entries(change.changes).map(([field, values]) => {
                            const v = values as { old: unknown; new: unknown }
                            return (
                              <p key={field} className="text-gray-600">
                                <span className="font-medium">{field}:</span>{' '}
                                <span className="line-through text-gray-400">{String(v.old || '-')}</span>
                                {' → '}
                                <span>{String(v.new || '-')}</span>
                              </p>
                            )
                          })}
                        </div>
                      )}

                      {change.comment && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs italic">
                          "{change.comment}"
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      {editDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{t('form.editTitle')}</h3>
                <button onClick={() => {
                  setEditDialogOpen(false)
                  setFieldErrors({})
                  setEditCostCenterSearch('')
                  updateMutation.reset()
                }}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Order Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.orderType')}
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="order_type"
                        value="non_urgent"
                        checked={editForm.order_type === 'non_urgent'}
                        onChange={(e) => setEditForm({ ...editForm, order_type: e.target.value as OrderType })}
                        className="mr-2"
                      />
                      {t('orderType.non_urgent')}
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="order_type"
                        value="emergency"
                        checked={editForm.order_type === 'emergency'}
                        onChange={(e) => setEditForm({ ...editForm, order_type: e.target.value as OrderType })}
                        className="mr-2"
                      />
                      {t('orderType.emergency')}
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.ordererName')}
                    </label>
                    <input
                      type="text"
                      value={editForm.orderer_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, orderer_name: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.ordererPhone')}
                    </label>
                    <input
                      type="tel"
                      value={editForm.orderer_phone || ''}
                      onChange={(e) => setEditForm({ ...editForm, orderer_phone: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div ref={editCostCenterDropdownRef} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.costCenter')}
                    </label>
                    <input
                      type="text"
                      value={editCostCenterSearch}
                      onChange={(e) => {
                        setEditCostCenterSearch(e.target.value)
                        setShowEditCostCenterDropdown(true)
                        // Clear selection if user starts typing
                        if (editForm.cost_center_id) {
                          setEditForm({ ...editForm, cost_center_id: undefined })
                        }
                      }}
                      onFocus={() => setShowEditCostCenterDropdown(true)}
                      placeholder={t('form.searchCostCenter')}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    {showEditCostCenterDropdown && (
                      <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto mt-1">
                        {filteredEditCostCenters.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            {t('form.noCostCenter')}
                          </div>
                        ) : (
                          filteredEditCostCenters.map((cc) => (
                            <div
                              key={cc.id}
                              onClick={() => {
                                setEditForm({ ...editForm, cost_center_id: cc.id })
                                setEditCostCenterSearch(`${cc.code} - ${cc.name}`)
                                setShowEditCostCenterDropdown(false)
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              {cc.code} - {cc.name}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.pickupDepartment')}
                    </label>
                    <input
                      type="text"
                      value={editForm.pickup_department || ''}
                      onChange={(e) => setEditForm({ ...editForm, pickup_department: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.patientName')}
                    </label>
                    <input
                      type="text"
                      value={editForm.patient_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, patient_name: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  {/* order_id_number is auto-generated (starting from 1000), not editable */}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.patientSsn')}
                  </label>
                  <input
                    type="text"
                    value={editForm.patient_ssn || ''}
                    onChange={(e) => {
                      setEditForm({ ...editForm, patient_ssn: e.target.value.toUpperCase() })
                      setSsnError('')
                    }}
                    className={`w-full border rounded-lg px-3 py-2 ${ssnError ? 'border-red-500' : ''}`}
                  />
                  {ssnError && <p className="text-xs text-red-500 mt-1">{ssnError}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.pickupLocation')}
                  </label>
                  <input
                    type="text"
                    value={editForm.pickup_location || ''}
                    onChange={(e) => setEditForm({ ...editForm, pickup_location: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.destinationLocation')}
                  </label>
                  <input
                    type="text"
                    value={editForm.destination_location || ''}
                    onChange={(e) => setEditForm({ ...editForm, destination_location: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.pickupDate')}
                    </label>
                    <input
                      type="date"
                      value={editForm.pickup_date || ''}
                      onChange={(e) => setEditForm({ ...editForm, pickup_date: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.pickupTime')}
                    </label>
                    <input
                      type="time"
                      value={editForm.pickup_time || ''}
                      onChange={(e) => setEditForm({ ...editForm, pickup_time: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.editComment')}
                  </label>
                  <textarea
                    value={editComment}
                    onChange={(e) => setEditComment(e.target.value)}
                    placeholder={t('form.editCommentPlaceholder')}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {updateMutation.isError && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg mt-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>{getErrorMessage(updateMutation.error)}</span>
                  </div>
                  {Object.keys(fieldErrors).length > 1 && (
                    <ul className="mt-2 ml-7 text-sm list-disc">
                      {Object.entries(fieldErrors).slice(1).map(([field, msg]) => (
                        <li key={field}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setEditDialogOpen(false)
                    setFieldErrors({})
                    setEditCostCenterSearch('')
                    updateMutation.reset()
                  }}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateMutation.isPending ? tc('saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Dialog */}
      {reviewDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{t('review.confirmTitle')}</h3>
              <button onClick={() => setReviewDialogOpen(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">{t('review.confirmMessage')}</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('review.comment')}
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={t('review.commentPlaceholder')}
                rows={3}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {reviewMutation.isError && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{getReviewErrorMessage(reviewMutation.error)}</span>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReviewDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReview}
                disabled={reviewMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('review.markAsReviewed')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Dialog */}
      {commentDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{t('comment.title')}</h3>
              <button onClick={() => setCommentDialogOpen(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('comment.label')}
              </label>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('comment.placeholder')}
                rows={4}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {commentMutation.isError && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{getCommentErrorMessage(commentMutation.error)}</span>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCommentDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAddComment}
                disabled={commentMutation.isPending || !newComment.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t('comment.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
